import React, { useEffect, useState, useRef } from "react";
import { motion, useInView, animate } from "framer-motion";
import {
  TbBrain,
  TbAlertTriangle,
  TbShieldCheck,
  TbSparkles,
  TbUserPlus,
  TbChartBar,
  TbDeviceMobile,
  TbCash,
  TbSpeakerphone,
  TbChartLine,
  TbBox,
  TbBus,
  TbRocket,
  TbAtom,
  TbBook2,
  TbHome,
  TbReceipt,
  TbCalendarEvent,
} from "react-icons/tb";
import { HiArrowRight } from "react-icons/hi";
import SEO from "../../seo/SEO";
import { seoConfig } from "../../seo/seoConfig";
import Badge from "../../components/Badge/Badge";
import Card from "../../components/Card/Card";
import FeatureIcon from "../../components/FeatureIcon/FeatureIcon";
import CtaBanner from "../../components/CtaBanner/CtaBanner";
import StatBlock from "../../components/StatBlock/StatBlock";
import LazyImage from "../../components/LazyImage/LazyImage";
import styles from "./AIFeatures.module.css";
import Button from "../../components/Button/Button";
import { ifillip_personalized_teaching as dashImg3 } from "../../controller/Image";
import RoleFeatures from "../../components/RoleFeatures/RoleFeatures";

/* ============================================================
   COUNT-UP NUMBER   uses Framer's useInView + animate()
============================================================ */

const CountUpNumber = ({
  target,
  duration = 1.6,
  decimals = 0,
  prefix = "",
  suffix = "",
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(0, target, {
      duration,
      ease: "easeOut",
      onUpdate: (latest) => {
        setValue(
          decimals > 0
            ? parseFloat(latest.toFixed(decimals))
            : Math.round(latest),
        );
      },
    });
    return () => controls.stop();
  }, [isInView, target, duration, decimals]);

  const display = decimals > 0 ? value.toFixed(decimals) : value;
  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};
const STAKEHOLDERS = [
  {
    title: "Teachers",
    description:
      "Smart recommendations and student tracking, with reduced workload.",
    icon: "fa-solid fa-chalkboard-user",
  },
  {
    title: "Students",
    description: "Adaptive learning and personalized growth.",
    icon: "fa-solid fa-graduation-cap",
  },
  {
    title: "Parents",
    description: "Transparent communication and real-time updates.",
    icon: "fa-solid fa-people-roof",
  },
  {
    title: "Management",
    description: "Data-driven insights and simplified operations.",
    icon: "fa-solid fa-chart-line",
  },
];
/* ---------- Decorative bar chart SVG ---------- */
/* ---------- Decorative bar chart SVG ---------- */
const RiskSpectrumChart = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const bars = [
    { h: 60, c: "#CBD5E1" },
    { h: 85, c: "#C7D2FE" },
    { h: 95, c: "#A5B4FC" },
    { h: 155, c: "#8B5CF6" },
    { h: 90, c: "#A5F3FC" },
    { h: 70, c: "#67E8F9" },
    { h: 95, c: "#5EEAD4" },
    { h: 55, c: "#CBD5E1" },
  ];

  return (
    <svg
      ref={ref}
      viewBox="0 0 360 180"
      className={styles.chartSvg}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="peakGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#A855F7" />
          <stop offset="100%" stopColor="#7C3AED" />
        </linearGradient>
      </defs>
      {bars.map((b, i) => {
        const finalY = 170 - b.h;
        const fill = i === 3 ? "url(#peakGrad)" : b.c;
        return (
          <motion.rect
            key={i}
            x={15 + i * 42}
            width={28}
            rx={6}
            fill={fill}
            initial={{ height: 0, y: 170 }}
            animate={
              isInView ? { height: b.h, y: finalY } : { height: 0, y: 170 }
            }
            transition={{ duration: 0.6, delay: i * 0.08, ease: "easeOut" }}
          />
        );
      })}
    </svg>
  );
};

const MiniBarChart = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const bars = [30, 55, 80, 100, 70, 60];

  return (
    <svg
      ref={ref}
      viewBox="0 0 240 120"
      className={styles.miniChart}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="miniGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#A855F7" />
          <stop offset="100%" stopColor="#6366F1" />
        </linearGradient>
      </defs>
      {bars.map((h, i) => {
        const finalY = 110 - h;
        return (
          <motion.rect
            key={i}
            x={20 + i * 34}
            width={24}
            rx={4}
            fill="url(#miniGrad)"
            initial={{ height: 0, y: 110 }}
            animate={
              isInView ? { height: h, y: finalY } : { height: 0, y: 110 }
            }
            transition={{ duration: 0.6, delay: i * 0.08, ease: "easeOut" }}
          />
        );
      })}
    </svg>
  );
};

/* ---------- Orbit diagram ---------- */
const OrbitDiagram = () => (
  <div className={styles.orbit}>
    <div className={styles.orbitRing}>
      <span className={styles.orbitDot} style={{ top: "0px", left: "50%" }} />
      <span
        className={styles.orbitDot}
        style={{ bottom: "-12px", left: "50%", background: "#4F6BFF" }}
      />
    </div>
    <div className={styles.orbitCenter}>
      <div className={styles.orbitIcon}>
        <svg viewBox="0 0 48 48" width="36" height="36">
          <circle cx="24" cy="24" r="4" fill="#4F6BFF" />
          <circle cx="10" cy="14" r="3" fill="#8B5CF6" />
          <circle cx="38" cy="14" r="3" fill="#22D3EE" />
          <circle cx="10" cy="34" r="3" fill="#22D3EE" />
          <circle cx="38" cy="34" r="3" fill="#8B5CF6" />
          <line
            x1="24"
            y1="24"
            x2="10"
            y2="14"
            stroke="#94A3B8"
            strokeWidth="1.5"
          />
          <line
            x1="24"
            y1="24"
            x2="38"
            y2="14"
            stroke="#94A3B8"
            strokeWidth="1.5"
          />
          <line
            x1="24"
            y1="24"
            x2="10"
            y2="34"
            stroke="#94A3B8"
            strokeWidth="1.5"
          />
          <line
            x1="24"
            y1="24"
            x2="38"
            y2="34"
            stroke="#94A3B8"
            strokeWidth="1.5"
          />
        </svg>
      </div>
      <div className={styles.orbitLabel}>CORE INTELLIGENCE</div>
      <div className={styles.orbitSub}>ACTIVE</div>
    </div>
  </div>
);

const AIFeatures = () => {
  return (
    <>
      <SEO {...seoConfig.aiSuite} />
      <div className={styles.page}>
        {/* ============ HERO ============ */}
        <section className={`${styles.hero} page-container`}>
          <div className="row align-items-start g-5">
            <div className="col-12 col-lg-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Badge variant="cyan">AI for Education</Badge>
                <h1 className={styles.heroTitle}>
                  Upgrade Your School with Leading{" "}
                  <span className="gradientText">
                    AI-Powered Education and School Management System
                  </span>
                </h1>
              </motion.div>
            </div>

            <div className="col-12 col-lg-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.15 }}
              >
                <Card
                  variant="default"
                  padding="lg"
                  className={styles.heroCard}
                >
                  <div className={styles.chartHeader}>
                    <span className={styles.chartLabel}>
                      STUDENT PERFORMANCE INSIGHTS
                    </span>
                    <span className={styles.chartMode}>CLASS_9A</span>
                  </div>
                  <RiskSpectrumChart />
                  <div className={styles.chartFooter}>
                    <div className={styles.chartStat}>
                      <FeatureIcon icon={TbSparkles} tint="purple" size="sm" />
                      <div>
                        <div className={styles.chartStatValue}>
                          <CountUpNumber
                            target={94.2}
                            decimals={1}
                            suffix="%"
                          />
                        </div>
                        <div className={styles.chartStatLabel}>
                          Prediction Accuracy
                        </div>
                      </div>
                    </div>
                    <button className={styles.detailsBtn}>
                      See How It Works
                    </button>
                  </div>
                </Card>
              </motion.div>
            </div>
          </div>
          <p className="normalText">
            Modern educational institutions need more than basic digital tools
            to meet the evolving demands of today's learning environment.
            AI-powered education platforms enhance learning effectiveness,
            support growth of institutions, improve overall communication and
            also simplify the administrative function. Compared to traditional
            school management systems, AI-driven platforms deliver personalized
            learning experiences, streamline workflows and offer data-driven
            insights that help institutions deliver high-quality education.
          </p>
          <p className="normalText mt-3">
            With schools, colleges, and educational institutions increasingly
            adopting intelligent solutions, there is a marked improvement in
            teaching effectiveness and student outcomes. Apart from automating
            routine tasks and reducing manual workloads, it provides educators
            with data points that needs focus . The use of AI in learning and
            management systems has helped institutions enhance operational
            efficiency, drive academic excellence, and remain ahead in a
            fast-changing world.
          </p>
          <section className="page-container missionSection px-0">
            <Card variant="default" padding="lg" className="missionCard">
              <div className="row align-items-center g-5">
                {/* LEFT CONTENT */}
                <div className="col-12 col-lg-7">
                  <h2 className="missionTitle">
                    iFillip: A Complete Education and Smart School Management
                    System
                  </h2>

                  <p className="normalText">
                    iFillip is a comprehensive and user-friendly smart school
                    management System that brings all the academic and
                    administrative operations together in a single platform.
                    Compared to other legacy systems, ifillip advanced AI
                    capabilities helps unify workflows, improves decision-making
                    and ensures better coordination across various stakeholders.
                  </p>

                  <p className="normalText mt-3">
                    The power of iFillip lies in its custom-built AI-powered
                    algorithm designed to support the school educators and
                    administrators to focus on delivering quality education,
                    creating personalized teaching and learning experiences.
                  </p>
                </div>

                {/* RIGHT SIDE */}
                <div className="col-12 col-lg-5 text-center">
                  <div className="missionCircle">
                    <div className="missionCirclePct">
                      <CountUpNumber target={100} suffix="%" />
                    </div>
                    <div className="missionCircleLabel">
                      SCHOOL TRANSFORMATION
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </section>
        </section>

        {/* AI-Based Personalized Teaching Section */}
        <section className="full-width-white-section">
          <div className={`page-container ${styles.heroSection}`}>
            <div className="row align-items-center g-5">
              <div className="col-12">
                <h1 className={styles.heroTitle}>
                  <span className="gradientText">
                    AI-Assisted Personalized Teaching
                  </span>{" "}
                  To Empower Educators
                </h1>
              </div>
              <div className="col-12">
                <motion.div
                  className={styles.heroDash}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.7, delay: 0.1 }}
                >
                  <LazyImage
                    {...dashImg3}
                    alt="iFillip school management platform dashboard showing real-time process automation and institutional health metrics"
                    wrapperClassName={styles.dashImg1}
                  />
                  <div className={styles.consensusChip}>
                    <FeatureIcon icon={TbAtom} tint="purple" size="sm" />
                    <div>
                      <div className={styles.chipLabel}>OPERATIONAL HEALTH</div>
                      <div className={styles.chipValue}>
                        <CountUpNumber
                          target={94.2}
                          decimals={1}
                          suffix="% Process Automation"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
              <div className="col-12">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <section className={`page-container px-0`}>
                    <div className={styles.card}>
                      <p className="normalText">
                        iFillip provides teachers with smart tools that help
                        them teach and engage students more effectively. By
                        combining academic planning and assessment management,
                        teachers can focus more on teaching and less on
                        administrative tasks.
                      </p>
                      <p className="normalText">
                        The advanced AI-based algorithm embedded in iFillip
                        assists teachers with:
                      </p>
                      <ul className={styles.list}>
                        <li>
                          Create and manage lesson plans quickly and easily with
                          real-time tracking of syllabus coverage.
                        </li>
                        <li>
                          Develop daily assessments that foster a study habit,
                          improve classroom discipline, and extend beyond the
                          classroom.
                        </li>
                        <li>
                          Swift analysis of assessment performance of individual
                          students preparing remedial learning materials and
                          follow-up assessments of those students requiring
                          additional help.
                        </li>
                        <li>
                          Identifying learning gaps and recommending targeted
                          interventions to provide individual attention and
                          support to improve academic outcomes.
                        </li>
                        <li>
                          Provide personalized teaching experiences at scale
                          using AI educational tools thereby saving time.
                        </li>
                        <li>
                          iFillip is an avant-garde AI-based learning platform
                          that boosts teacher productivity, supports
                          personalized learning, and improves students'
                          performance.
                        </li>
                      </ul>
                      {/* <p className="normalText">
                     Provide personalized teaching experiences at scale using AI educational tools thereby saving time. 
                    </p> */}
                    </div>
                  </section>
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ ADAPTIVE LEARNING SECTION ============ */}
        <section className={`${styles.forecast} page-container`}>
          <motion.header
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className={styles.forecastHeader}
          >
            <h2 className={styles.sectionTitle}>
              AI-Based Adaptive Learning Built Into Every Lesson
            </h2>
            <p className={styles.sectionLead}>
              One of the salient features of iFillip is its customized smart
              learning system. Since every student's learning pattern is unique
              and distinct, the learning experience needs to be customized for
              individual students.
            </p>
          </motion.header>

          <div className="row g-4">
            <div className="col-12 col-lg-4">
              <Card
                variant="default"
                padding="lg"
                className={styles.cohortCard}
              >
                <div className={styles.cohortHeadRow}>
                  <h3 className="subtitle">Personalized Learning Trajectory</h3>
                  <svg width="48" height="24" viewBox="0 0 48 24" fill="none">
                    <path
                      d="M2 18 L12 12 L22 14 L32 6 L46 4"
                      stroke="#8B5CF6"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p className="normalText">
                  Based on student performance, iFillip's adaptive engine
                  automatically adjusts the difficulty level, learning path, and
                  practice content in real-time.
                </p>
              </Card>
            </div>

            <div className="col-12 col-lg-8">
              <Card
                variant="gradient"
                padding="lg"
                className={styles.scenarioCard}
              >
                <h3 className={styles.cardTitle} style={{ color: "#fff" }}>
                  Improved Learning Outcomes
                </h3>
                <p
                  className="normalText"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  Using AI tools in Education, iFillip helps students improve
                  their learning habits, stay focussed on their studies and
                  complete assessments on time. Regular and continuous
                  monitoring of performance and right academic support enhances
                  academic achievement while enabling a more effective and
                  meaningful learning experience.
                </p>
                <div className={styles.pillRow}>
                  <span className={styles.darkPill}>
                    Better Study Habits +
                    <CountUpNumber target={45} suffix="%" />
                  </span>
                  <span className={styles.darkPill}>
                    Improved Performance +
                    <CountUpNumber target={35} suffix="%" />
                  </span>
                </div>
              </Card>
            </div>
          </div>

          <div className="row g-4 mt-2">
            <div className="col-12 col-lg-12">
              <div className="row g-4 h-100">
                <div className="col-5">
                  <Card
                    variant="cyan"
                    padding="md"
                    className={styles.smallFeatureCard}
                  >
                    <FeatureIcon icon={TbSparkles} tint="cyanSolid" size="md" />
                    <h4 className="subtitle">
                      AI-Based Adaptive Learning Built Into Every Lesson
                    </h4>
                    <p
                      className="normalText"
                      style={{ color: "rgba(255,255,255,0.85)" }}
                    >
                      Difficulty levels and learning sequences adapt instantly
                      to each student's performance.
                    </p>
                  </Card>
                </div>
                <div className="col-7">
                  <Card
                    variant="default"
                    padding="md"
                    className={styles.smallFeatureCard}
                  >
                    <FeatureIcon icon={TbShieldCheck} tint="cyan" size="md" />
                    <h4 className="subtitle">Customized Practice Content</h4>
                    <p className="normalText">
                      Practice materials are generated in real-time based on
                      individual student needs.
                    </p>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ AI CLASSROOM ASSISTANT ============ */}
        <section className="full-width-white-section">
          <div className={`${styles.adaptive} page-container`}>
            <Card
              variant="default"
              padding="lg"
              className={styles.adaptiveCard}
            >
              <div className="row align-items-center g-5">
                <div className="col-12 col-md-6">
                  <h3 className={styles.cardTitle}>
                    AI-Based Data Driven Analytics and Insights
                  </h3>
                  <p className="normalText">
                    As an advanced{" "}
                    <a href="/core-features" className="companyLink">
                      AI based learning platform
                    </a>
                    , iFillip comes with intelligent analytics that transforms
                    raw data into meaningful insights helping teachers, parents,
                    and school management to make informed data driven
                    decisions.
                  </p>
                  <p className="normalText mt-3">
                    The AI engine generates an Academic Health Index on each
                    student's academic performance based on attendance, test
                    scores, examination results and classroom participation. It
                    provides teachers with visibility on individual students`
                    subject wise and overall performance, learning gaps,
                    immediate improvement actions, and future academic outcome
                    prediction allowing teachers and administrators to make
                    timely interventions, track progress and drive continuous
                    improvement.
                  </p>
                  <div className={styles.statsRow}>
                    <StatBlock
                      value={
                        <>
                          <CountUpNumber target={2.4} decimals={1} />x
                        </>
                      }
                      label="Faster Concept Mastery"
                      variant="gradient"
                    />
                    <StatBlock
                      value={<CountUpNumber target={88} suffix="%" />}
                      label="Student Retention Rate"
                      variant="gradient"
                    />
                  </div>
                </div>
                <div className="col-12 col-md-6 text-center">
                  <OrbitDiagram />
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Role Features Section - Preserved exactly as is */}
        <div>
          <RoleFeatures />
        </div>

        {/* Conclusion Section */}
        <section className="full-width-white-section">
          <div className={`page-container ${styles.conclusionSection}`}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className={styles.card}>
                <h2 className={styles.sectionTitle}>
                  The Future of Education, Powered by AI
                </h2>
                <p className="normalText">
                  iFillip redefines education through AI and automation. This
                  futuristic AI based learning platform automates school
                  academic and administrative tasks to enable schools operate
                  more efficiently and with better learning results.
                </p>

                <p className="normalText mt-4">
                  iFillip unites teachers, parents, and school administrators on
                  one platform. It encourages collaboration, informed
                  decision-making and transparency resulting in a smarter, more
                  connected environment where everyone contributes to the
                  overall success.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* ============ CTA BANNER ============ */}
        <section className={`${styles.ctaWrap} page-container mt-5`}>
          <CtaBanner
            title="Transform Your Educational Institution with iFillip"
            // subtitle="It's time to take a step forward and transform your educational institution with a smarter, future-ready learning environment. Experience iFillip and discover how it can redefine teaching, learning, and school administration through the power of AI in education. Upgrade to a truly intelligent AI-based learning platform and move closer towards educational excellence. With iFillip, modernize your educational institution workflows, improve learning experiences, and prepare students for a technology-driven future."
            primaryLabel="Book a Demo"
            primaryHref="/book-demo"
          />
        </section>
      </div>
    </>
  );
};

export default AIFeatures;
